import { css, keyframes, styled, type RuleSet } from 'styled-components';

import { isWinchMovementFeedbackKind } from '@/components/cameraStreamWidget/cameraStreamWidgetWrapper/cameraMovementFeedback/cameraMovementFeedback.helpers.ts';
import type {
  CameraMovementFeedbackKind,
  WinchMovementFeedbackKind,
} from '@/components/cameraStreamWidget/cameraStreamWidgetWrapper/cameraMovementFeedback/cameraMovementFeedback.types.ts';

import {
  CAMERA_MOVEMENT_FEEDBACK_EDGE_INSET,
  MOVEMENT_FEEDBACK_ICON_COLOR,
  MOVEMENT_FEEDBACK_PLACEMENT,
  MOVEMENT_FEEDBACK_VISIBLE_MS,
  WINCH_COMBO_FLEX,
  WINCH_MOVEMENT_FEEDBACK_EDGE_INSET,
  type CameraMovementFeedbackPlacement,
} from './cameraMovementFeedback.constants.ts';

const feedbackPulse = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.85);
  }
  12% {
    opacity: 1;
    transform: scale(1);
  }
  70% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: scale(1.05);
  }
`;

const placementCss: Record<CameraMovementFeedbackPlacement, (inset: string) => RuleSet<object>> = {
  top: inset => css`
    top: ${inset};
    left: 50%;
    transform: translateX(-50%);
  `,
  bottom: inset => css`
    bottom: ${inset};
    left: 50%;
    transform: translateX(-50%);
  `,
  left: inset => css`
    left: ${inset};
    top: 50%;
    transform: translateY(-50%);
  `,
  right: inset => css`
    right: ${inset};
    top: 50%;
    transform: translateY(-50%);
  `,
  center: () => css`
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  `,
};

const getEdgeInsetForKind = (kind: CameraMovementFeedbackKind): string =>
  isWinchMovementFeedbackKind(kind)
    ? WINCH_MOVEMENT_FEEDBACK_EDGE_INSET
    : CAMERA_MOVEMENT_FEEDBACK_EDGE_INSET;

const getPlacementCssForKind = (kind: CameraMovementFeedbackKind): RuleSet<object> => {
  const placement = MOVEMENT_FEEDBACK_PLACEMENT[kind];
  const inset = getEdgeInsetForKind(kind);
  return placementCss[placement](inset);
};

export const OverlayRoot = styled.div<{ $zIndex: number }>`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: ${({ $zIndex }) => $zIndex};
`;

export const PlacementHost = styled.div<{ $kind: CameraMovementFeedbackKind }>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ $kind }) => getPlacementCssForKind($kind)}
`;

const movementFeedbackIconSvgStyles = css`
  /* reset.css sets svg { color: var(--sui-text-neutral-contrast) }; override for stream overlay */
  && svg {
    color: ${MOVEMENT_FEEDBACK_ICON_COLOR};
  }
`;

export const IconPulse = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.75));
  ${movementFeedbackIconSvgStyles}
  animation: ${feedbackPulse} ${MOVEMENT_FEEDBACK_VISIBLE_MS}ms ease-out forwards;
`;

export const WinchIconCombo = styled.div<{ $kind: WinchMovementFeedbackKind }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  flex-direction: ${({ $kind }) => WINCH_COMBO_FLEX[$kind]};
  ${movementFeedbackIconSvgStyles}
`;
